import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import { useModal } from '@/contexts/modal'
import { useConfirmation } from '@/contexts/confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { useTableSettings } from '@/hooks/useTableSettings'

import {
    cancelDayOff,
    getDayOffsList,
    getDayOffsEmployeeList,
} from '../services'
import { DAY_OFF_STATUS } from '../constants'
import { DayOffResponse } from '../types/response'
import { getDayOffListColumns } from '../components/List/columns'
import {
    getExpandedColumns,
    getHrmActions,
    getMemberActions,
    getSelectionActions,
    getFilterItems,
    getFilterToggles,
    getPreviewColumns,
} from '../utils/dayoff-list-config'

interface DayOffFilters {
    day_off_type: string
    day_off_status: string
    department_id: number
    employee_ids: number[]
    day_off_item_date: string[] // [from, to] range
    day_off_item_date_to: string // form-only for daterangepicker UI (not sent to API)
}

export const useDayOffList = (pageType: 'member' | 'hrm') => {
    const navigate = useNavigate()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const { t } = useTranslation('dayoffs')

    // Selection state
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    // Table settings
    const { filterOptions, getHiddenOptions } = useTableSettings('dayoff-list')
    const hiddenEmployeeOptions = getHiddenOptions('employeeOptions')
    const employeeActiveOnly = !hiddenEmployeeOptions.includes('activeOnly')
    const statusOptions = filterOptions(
        'statusOptions',
        DAY_OFF_STATUS.map((item) => ({
            label: t(item.label),
            value: item.value,
        }))
    )

    // URL params & form
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        setFilters,
        searchInputProps,
    } = useUrlSearchParams<DayOffFilters>({
        defaults: { sort_by: 'day_off_id', sort_order: 'desc' },
        filterDefaults: {
            day_off_type: '',
            day_off_status: '',
            department_id: 0,
            employee_ids: [],
            day_off_item_date: [],
            day_off_item_date_to: '',
        },
    })

    const { control, setValue } = useForm<DayOffFilters>({
        defaultValues: filters,
    })

    // Data fetching
    const apiCall = pageType === 'hrm' ? getDayOffsList : getDayOffsEmployeeList
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DAYOFFS, pageType, queryParams],
        queryFn: () => apiCall(queryParams),
        placeholderData: true,
    })

    // Cancel mutation
    const { mutateAsync: doCancelDayOff } = useMutationWithHandler(
        { mutationFn: cancelDayOff },
        {
            reactQueryKeys: QUERY_KEYS.DAYOFFS,
            errorMessage: 'something went wrong',
            onSuccess: () => refetch(),
        }
    )

    // Cancel handler
    const handleCancel = useCallback(
        (row: DayOffResponse) => {
            setConfirmation({
                open: true,
                title: 'dayoffs.cancel',
                description: 'dayoffs.cancel_confirmed',
                onConfirm: async () => {
                    try {
                        await doCancelDayOff(row?.day_off_id)
                        toast.success('Cancel day off success')
                    } catch (error) {
                        console.error(error)
                    }
                },
            })
        },
        [setConfirmation, doCancelDayOff]
    )

    // Memoized configs
    const columns = useMemo(() => getDayOffListColumns(t), [t])
    const expandedColumns = useMemo(() => getExpandedColumns(t), [t])

    const actions = useMemo(
        () =>
            pageType === 'hrm'
                ? getHrmActions({ t, setModal, refetch })
                : getMemberActions({ t, navigate, onCancel: handleCancel }),
        [t, pageType, setModal, refetch, navigate, handleCancel]
    )

    const selectionActions = useMemo(
        () =>
            getSelectionActions({
                t,
                setModal,
                refetch,
                clearSelection: () => setSelectedKeys([]),
            }),
        [t, setModal, refetch]
    )

    const filterItems = useMemo(
        () =>
            getFilterItems({
                t,
                pageType,
                statusOptions,
                filters,
                employeeActiveOnly,
                setValue,
                setFilters,
            }),
        [
            t,
            pageType,
            statusOptions,
            filters,
            employeeActiveOnly,
            setValue,
            setFilters,
        ]
    )

    const filterToggles = useMemo(
        () => getFilterToggles(t, pageType),
        [t, pageType]
    )
    const previewColumns = useMemo(() => getPreviewColumns(t), [t])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        dayoffs: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        // Form & params
        control,
        setFilter,
        params,
        queryParams,
        searchInputProps,

        // Selection
        selectedKeys,
        setSelectedKeys,

        // Configs
        columns,
        expandedColumns,
        actions,
        selectionActions,
        filterItems,
        filterToggles,
        previewColumns,
        employeeActiveOnly,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        navigate,
        t,
    }
}
